import { type FormEvent, useRef } from 'react';

import { Banner, Flex, Text } from '@spotify-internal/encore-web';
import {
  AuthScreen,
  type FormFieldsHandle,
  FormFieldsProvider,
} from '@spotify/confidence-core-react';

import type { SurveyAnswers } from '../../config/onboardingSurvey';
import { CustomRoleFormField } from './CustomRoleFormField';
import { RoleFormField } from './RoleFormField';
import { SurveyActions } from './SurveyActions';
import { type SurveyFormValues, toSurveyAnswers } from './surveyForm';

import styles from './OnboardingSurvey.module.css';

type OnboardingSurveyProps = {
  workspaceName?: string;
  isSaving: boolean;
  hasSaveError: boolean;
  onSave: (answers: SurveyAnswers) => void;
};

export const OnboardingSurvey = ({
  workspaceName,
  isSaving,
  hasSaveError,
  onSave,
}: OnboardingSurveyProps) => {
  const formFieldsRef = useRef<FormFieldsHandle<SurveyFormValues>>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formFieldsRef.current;
    if (!form) {
      return;
    }
    onSave(toSurveyAnswers(form.values));
  };

  return (
    <AuthScreen.Root>
      <AuthScreen.Main>
        <Flex direction="column" gap="looser" className={styles.layout}>
          <Flex direction="column" gap="looser" className={styles.welcome}>
            <AuthScreen.Logo />
            <AuthScreen.Title>Before you jump in</AuthScreen.Title>
            <AuthScreen.Description>
              Confidence would like to know a bit more about you, so it can
              tailor{' '}
              {workspaceName ? (
                <Text
                  as="span"
                  variant="bodyMediumBold"
                  semanticColor="textBase"
                >
                  {workspaceName}
                </Text>
              ) : (
                'your workspace'
              )}{' '}
              to how you work.
            </AuthScreen.Description>
          </Flex>

          <AuthScreen.Card>
            <form onSubmit={handleSubmit} data-track-source-anchor="d26267a90b">
              <FormFieldsProvider<SurveyFormValues> ref={formFieldsRef}>
                <Flex direction="column" gap="looser">
                  {hasSaveError && (
                    <Banner colorSet="negative" contextual role="alert">
                      Something went wrong. Try again.
                    </Banner>
                  )}
                  <Flex direction="column" gap="tighter">
                    <RoleFormField />
                    <CustomRoleFormField />
                  </Flex>
                  <SurveyActions
                    isSaving={isSaving}
                    onSkip={() => onSave({})}
                  />
                </Flex>
              </FormFieldsProvider>
            </form>
          </AuthScreen.Card>
        </Flex>
      </AuthScreen.Main>
    </AuthScreen.Root>
  );
};
